import { builtInVariants } from '@ce/theme';
import type { TenantBundle } from '../types';

/**
 * The Nocturne Con bundle, exactly the content the design prototype shipped
 * with, expressed in the CMS contract. This is what the mock CMS serves and
 * what both apps fall back to offline, so a demo build works with no network.
 */
export const nocturneBundle: TenantBundle = {
  tenantId: 'nocturne-con',
  revision: '2026-09-01T00:00:00Z',

  theme: {
    brand: {
      name: 'Nocturne Con',
      shortName: 'Nocturne',
      logoUrl: null,
      splashImageUrl: null,
      splashBackground: '#161826',
      tagline: 'Convention companion',
    },
  },

  themeVariants: builtInVariants,

  promos: [
    {
      id: 'panels', title: 'Panels', subtitle: 'Hear the stories behind the fandom',
      imageUrl: 'assets/promos/panels.jpg', textAlign: 'left',
      alt: 'A panellist on stage with a microphone, addressing a packed hall.',
      target: { screen: 'schedule', track: 'Panel' },
    },
    {
      id: 'autographs', title: 'Autographs', subtitle: 'Meet your favorite guests',
      imageUrl: 'assets/promos/autographs.jpg', textAlign: 'left',
      alt: 'Guests in costume signing photographs at a long signing table.',
      target: { screen: 'talent' },
    },
    {
      id: 'photo-ops', title: 'Photo Ops', subtitle: 'Capture the moment',
      imageUrl: 'assets/promos/photo-ops.jpg', textAlign: 'left',
      alt: 'Three attendees taking a selfie together in front of a lit backdrop.',
      target: { screen: 'talent' },
    },
    {
      id: 'cosplay', title: 'Cosplay', subtitle: 'Show off your fandom style',
      imageUrl: 'assets/promos/cosplay.jpg', textAlign: 'left',
      alt: 'Four cosplayers in elaborate armour and robes posing under stage lights.',
      target: { screen: 'events' },
    },
    {
      id: 'exclusives', title: 'Exclusives', subtitle: 'Limited merch and rare drops',
      imageUrl: 'assets/promos/exclusives.jpg', textAlign: 'left',
      alt: 'A merchandise table stacked with mystery boxes, figures and prints.',
      target: { screen: 'hall', hallTab: 'exhibitors' },
    },
    {
      id: 'artist-alley', title: 'Artist Alley', subtitle: 'Discover original creators',
      imageUrl: 'assets/promos/artist-alley.jpg', textAlign: 'left',
      alt: 'Rows of artist tables hung with prints, with attendees browsing.',
      target: { screen: 'hall', hallTab: 'exhibitors', exZone: 'Artist Alley' },
    },
    {
      id: 'gaming', title: 'Gaming', subtitle: 'Play, compete, level up',
      imageUrl: 'assets/promos/gaming.jpg', textAlign: 'left',
      alt: 'Players at monitors in a tournament setup in front of a crowd.',
      target: { screen: 'schedule', track: 'Gaming' },
    },
    {
      id: 'screenings', title: 'Screenings', subtitle: 'Premieres, trailers, and more',
      imageUrl: 'assets/promos/screenings.jpg', textAlign: 'left',
      alt: 'An audience seated in front of a huge screen showing a fantasy landscape.',
      target: { screen: 'schedule', track: 'Screening' },
    },
    {
      id: 'workshops', title: 'Workshops', subtitle: 'Learn, build, and create',
      imageUrl: 'assets/promos/workshops.jpg', textAlign: 'left',
      alt: 'Makers painting armour and sketching at a crowded workshop bench.',
      target: { screen: 'schedule', track: 'Workshop' },
    },
    {
      id: 'meetups', title: 'Meetups', subtitle: 'Find your fandom family',
      imageUrl: 'assets/promos/meetups.jpg', textAlign: 'left',
      alt: 'A group of friends laughing around a table of figures and cards.',
      target: { screen: 'events' },
    },
  ],


  event: {
    id: 'nocturne-2026',
    name: 'Nocturne Con',
    venueName: 'Riverfront Center',
    dateRange: 'Sep 17–20',
    currency: '$',
    guestCount: 64,
    boothCount: 312,
    reminderLeadMinutes: 20,
  },

  days: [
    { key: 'thu', dow: 'THU', num: '17', label: 'Thursday 17', note: 'Preview night · 4pm–10pm', date: '2026-09-17' },
    { key: 'fri', dow: 'FRI', num: '18', label: 'Friday 18', note: 'Full programming · 9am–11pm', date: '2026-09-18' },
    { key: 'sat', dow: 'SAT', num: '19', label: 'Saturday 19', note: 'Busiest day · 8am–1am', date: '2026-09-19' },
    { key: 'sun', dow: 'SUN', num: '20', label: 'Sunday 20', note: 'Winds down 5pm', date: '2026-09-20' },
  ],

  tracks: ['All', 'Panel', 'Special', 'Autographs', 'Workshop', 'Screening', 'Gaming'],
  talentFilters: ['All', 'Sci-Fi', 'Fantasy', 'Anime', 'Comics', 'Tabletop', 'Cosplay'],

  talent: [
    {
      id: 'dara', initials: 'DV', name: 'Dara Vance', role: 'Capt. Rell — Starfall trilogy',
      fandoms: ['Starfall', 'Sci-Fi'], days: 'Fri–Sun', photoUrl: 'assets/talent/dara.png',
      prices: { photo: 95, auto: 75, combo: 150, selfie: 40 },
      bio: 'Lead of all three Starfall films and the upcoming series pilot. Appearing Friday through Sunday with two photo studios and a Saturday spotlight panel.',
    },
    {
      id: 'marcus', initials: 'MO', name: 'Marcus Oyelaran', role: 'Voice of Kirov — Ashen Reach',
      fandoms: ['Ashen Reach', 'Video games', 'Anime'], days: 'Sat–Sun', photoUrl: 'assets/talent/marcus.png',
      prices: { photo: 70, auto: 55, combo: 110, selfie: 40 },
      bio: 'Fifteen years of game and animation voice work, including Kirov across four Ashen Reach titles. Runs a live booth-recording demo on Sunday.',
    },
    {
      id: 'nia', initials: 'NT', name: 'Nia Tsukamoto', role: 'Creator — Hollow Bell',
      fandoms: ['Hollow Bell', 'Anime', 'Comics'], days: 'Thu–Sat', photoUrl: 'assets/talent/nia.png',
      prices: { photo: 60, auto: 65, combo: 110, selfie: 40 },
      bio: 'Writer and artist of Hollow Bell, nineteen volumes and counting. Sketching at Artist Alley table A-12 between panels.',
    },
    {
      id: 'theo', initials: 'TL', name: 'Theo Lindqvist', role: 'Halfling Chronicles ensemble',
      fandoms: ['Halfling Chronicles', 'Fantasy'], days: 'Fri–Sun', photoUrl: 'assets/talent/theo.png',
      prices: { photo: 85, auto: 70, combo: 135, selfie: 40 },
      bio: 'Nine months in prosthetics across two Halfling Chronicles films. Hosts Second Breakfast on Saturday morning — yes, there is a menu.',
    },
    {
      id: 'rosalind', initials: 'RA', name: 'Rosalind Achebe', role: 'Author — Hexwood series',
      fandoms: ['Hexwood', 'Fantasy', 'Horror'], days: 'Thu–Fri', photoUrl: 'assets/talent/rosalind.png',
      prices: { photo: 45, auto: 40, combo: 75, selfie: 40 },
      bio: 'Seven Hexwood novels and the graphic-novel adaptation. Reading and Q&A Thursday evening; signing limit of three items per attendee.',
    },
    {
      id: 'emil', initials: 'EB', name: 'Emil Brandt', role: 'Practical effects supervisor',
      fandoms: ['Horror', 'Sci-Fi'], days: 'Sat', photoUrl: 'assets/talent/emil.png',
      prices: { photo: 40, auto: 35, combo: 65, selfie: 40 },
      bio: 'Creature and gore work on eleven features. Brings a table of maquettes and runs a foam-latex workshop Saturday afternoon.',
    },
    {
      id: 'priya', initials: 'PR', name: 'Priya Raghunath', role: 'World cosplay champion 2025',
      fandoms: ['Cosplay', 'Fantasy'], days: 'Fri–Sun', photoUrl: 'assets/talent/priya.png',
      prices: { photo: 35, auto: 25, combo: 50, selfie: 40 },
      bio: 'Won worlds with a fully articulated Hexwood wing rig. Judges the Saturday runway and staffs the armor repair bar.',
    },
    {
      id: 'ivo', initials: 'IK', name: 'Ivo Kesler', role: 'Designer — Sixth Sense TCG',
      fandoms: ['Sixth Sense TCG', 'Tabletop'], days: 'Thu–Sun', photoUrl: 'assets/talent/ivo.png',
      prices: { photo: 30, auto: 30, combo: 50, selfie: 40 },
      bio: 'Lead designer on nine Sixth Sense sets. Playing pickup games at the Dice Tavern all weekend and previewing the winter set on Friday.',
    },
  ],

  sessions: [
    { id: 's1', day: 'thu', t: '16:30', end: '17:15', title: 'Opening remarks & weekend map', room: 'Main Stage', track: 'Panel', who: [], tags: [],
      desc: 'Ten minutes of housekeeping and forty of actual use: where the queues form, which rooms fill early, what changed since last year, and how the badge readers work. Worth the seat if it is your first Nocturne.',
      notes: ['Capacity 2,400', 'No queue'],
     },
    { id: 's2', day: 'thu', t: '18:00', end: '19:00', title: 'Hexwood: reading and Q&A', room: 'Panel Room 103', track: 'Panel', who: ['rosalind'], tags: ['Hexwood', 'Fantasy'],
      desc: 'Rosalind Achebe reads from the unpublished eighth Hexwood novel, then takes questions for the back half. She is generous with process questions and blunt about the ones she has answered a hundred times.',
      notes: ['Capacity 180', 'Reading + Q&A'],
     },
    { id: 's3', day: 'thu', t: '19:30', end: '21:00', title: 'Hollow Bell vol. 19 first look', room: 'Panel Room 102', track: 'Panel', who: ['nia'], tags: ['Hollow Bell', 'Anime'],
      desc: 'First look at volume nineteen, with page roughs projected and Nia Tsukamoto talking through the panel decisions that did not survive the edit. Spoilers for volume eighteen throughout.',
      notes: ['Capacity 300', 'Spoilers'],
     },
    { id: 's4', day: 'thu', t: '20:00', end: '23:30', title: 'Retro Arcade Lock-In', room: 'Gaming Hall', track: 'Special', who: [], tags: ['Video games'],
      desc: 'Forty cabinets on free play until half eleven, CRT console stations along the back wall, and a repair clinic in the corner that will look at whatever you brought in your bag.',
      notes: ['Badge only', 'Free play'],
     },
    { id: 's5', day: 'fri', t: '10:00', end: '11:00', title: 'Starfall: shooting the third act', room: 'Main Stage', track: 'Panel', who: ['dara'], tags: ['Starfall', 'Sci-Fi'],
      desc: 'The third act took nine weeks and two units. Dara Vance walks through the shoot with storyboards and a reel of takes that did not make it, including the one where the rig failed.',
      notes: ['Capacity 2,400', 'Clips shown'],
     },
    { id: 's6', day: 'fri', t: '11:30', end: '12:30', title: 'Autograph Row — session one', room: 'Autograph Row', track: 'Autographs', who: ['dara', 'theo', 'nia'], tags: ['Autographs'],
      desc: 'The first of the weekend\'s signing blocks. Three guests at the row, one item each unless you hold a combo voucher. Queue board at the entrance shows live wait times.',
      notes: ['One item each', 'Queue board'],
     },
    { id: 's7', day: 'fri', t: '13:00', end: '14:00', title: 'Sixth Sense winter set preview', room: 'Panel Room 104', track: 'Gaming', who: ['ivo'], tags: ['Sixth Sense TCG', 'Tabletop'],
      desc: 'Ivo Kesler previews the winter set with twelve cards revealed on stage and a rules-change explainer that will settle several arguments. Demo decks available afterwards.',
      notes: ['Capacity 180', 'Card reveals'],
     },
    { id: 's8', day: 'fri', t: '15:00', end: '16:30', title: 'Building a wing rig that folds', room: 'Workshop 201', track: 'Workshop', who: ['priya'], tags: ['Cosplay'],
      desc: 'Priya Raghunath built a wing rig that folds to fit through a door, and this is the hour where she shows exactly how. Bring a notebook; the hinge geometry is the whole talk.',
      notes: ['Capacity 60', 'Ticketed', 'Hands-on'],
     },
    { id: 's9', day: 'fri', t: '20:00', end: '22:00', title: 'Ashen Reach: the cutscene reel', room: 'Main Stage', track: 'Screening', who: ['marcus'], tags: ['Ashen Reach', 'Video games'],
      desc: 'Every cutscene from the four Ashen Reach titles, cut together in order, with Marcus Oyelaran doing live commentary over the top. Two hours, one interval, no spoiler warnings.',
      notes: ['Capacity 2,400', 'Runs 2 hours'],
     },
    { id: 's10', day: 'sat', t: '09:00', end: '10:30', title: 'Second Breakfast with the Halflings', room: 'Grand Ballroom', track: 'Special', who: ['theo'], tags: ['Halfling Chronicles', 'Fantasy'],
      desc: 'Seven courses, in character, at nine in the morning. Theo Lindqvist and three of the ensemble host each table in rotation, so everyone gets a turn. Dietary notes taken at booking.',
      notes: ['Ticketed', 'Seated', '38 seats left'],
     },
    { id: 's11', day: 'sat', t: '11:00', end: '12:00', title: 'Dara Vance spotlight', room: 'Main Stage', track: 'Panel', who: ['dara'], tags: ['Starfall', 'Sci-Fi'],
      desc: 'An hour with the lead of all three Starfall films, covering the trilogy, the series pilot, and the parts of both she is allowed to talk about. Audience questions from the floor mics.',
      notes: ['Capacity 2,400', 'Floor mics'],
     },
    { id: 's12', day: 'sat', t: '11:20', end: '12:40', title: 'Photo Op Studio A — Vance', room: 'Photo Op Studio A', track: 'Autographs', who: ['dara'], tags: ['Photo ops'],
      desc: 'Studio A, duo and group sets available. Prints are collected at the print desk thirty minutes after the shoot; the digital download lands in your app the same evening.',
      notes: ['Voucher required', 'Prints at desk'],
     },
    { id: 's13', day: 'sat', t: '13:30', end: '15:00', title: 'Foam latex from mold to face', room: 'Workshop 202', track: 'Workshop', who: ['emil'], tags: ['Horror', 'Cosplay'],
      desc: 'Emil Brandt runs a mould-to-face foam latex piece in ninety minutes, start to finish, on a volunteer. Messy, technical, and the best hour of the weekend if this is your thing.',
      notes: ['Capacity 60', 'Ticketed', 'Messy'],
     },
    { id: 's14', day: 'sat', t: '16:00', end: '17:00', title: 'Drawing Hollow Bell live', room: 'Panel Room 102', track: 'Panel', who: ['nia'], tags: ['Hollow Bell', 'Original art'],
      desc: 'Nia Tsukamoto draws a full Hollow Bell page live on the document camera while talking through it. The finished page goes to the Sunday charity auction.',
      notes: ['Capacity 300', 'Auction lot'],
     },
    { id: 's15', day: 'sat', t: '21:00', end: '23:00', title: 'Midnight Cosplay Runway', room: 'Grand Ballroom', track: 'Special', who: ['priya'], tags: ['Cosplay'],
      desc: 'Open-entry runway judged by Priya Raghunath. Walk-ons close at 20:30 and spectators are seated from 20:45. Bring a repair kit; the armor bar stays open through the show.',
      notes: ['Free', 'Entries close 20:30'],
     },
    { id: 's16', day: 'sun', t: '10:00', end: '11:00', title: 'Voice booth, live', room: 'Panel Room 101', track: 'Panel', who: ['marcus'], tags: ['Ashen Reach', 'Anime'],
      desc: 'Marcus Oyelaran records live in a booth on stage, taking direction from the audience and showing what fifteen years of game VO actually sounds like between takes.',
      notes: ['Capacity 300', 'Live recording'],
     },
    { id: 's17', day: 'sun', t: '12:00', end: '13:30', title: 'Bring-Your-Own-Dice Tavern', room: 'Gaming Hall', track: 'Special', who: ['ivo'], tags: ['Tabletop'],
      desc: 'Twenty open tables, rotating GMs, one-shots starting on the half hour. Ivo Kesler plays pickup games all afternoon. Dice provided if you forgot yours, which you did.',
      notes: ['Free', 'Drop in'],
     },
    { id: 's18', day: 'sun', t: '14:00', end: '15:00', title: 'Charity auction: props & pages', room: 'Main Stage', track: 'Special', who: ['emil', 'nia'], tags: ['Collecting'],
      desc: 'Screen-used maquettes from Emil Brandt and original Hollow Bell pages under the hammer. Preview from 13:00. All proceeds to the city literacy fund.',
      notes: ['Free to attend', 'Preview 13:00'],
     },
    { id: 's19', day: 'sun', t: '15:30', end: '16:30', title: 'Closing panel: what we saw', room: 'Main Stage', track: 'Panel', who: [], tags: [],
      desc: 'The closing hour: what worked, what broke, what is coming next year, and the presale window. Short, and the room empties straight into the load-out.',
      notes: ['Capacity 2,400', 'Presale info'],
     },
  ],

  specialEvents: [
    { id: 'e1', ref: 's10', kicker: 'Saturday · ticketed', title: 'Second Breakfast with the Halflings', desc: 'A seated second breakfast with Theo Lindqvist and three of the Halfling Chronicles ensemble. Seven courses, in character, at nine in the morning. Dietary notes taken at booking.', when: 'Sat 9:00', room: 'Grand Ballroom', price: '$68', status: '38 seats left', hot: true, level: '2' },
    { id: 'e2', ref: 's15', kicker: 'Saturday night · free', title: 'Midnight Cosplay Runway', desc: 'Open-entry runway judged by Priya Raghunath. Walk-ons close at 20:30; spectators seated from 20:45.', when: 'Sat 21:00', room: 'Grand Ballroom', price: 'Free', status: 'Entries open', hot: false, level: '2' },
    { id: 'e3', ref: 's4', kicker: 'Thursday night · badge only', title: 'Retro Arcade Lock-In', desc: 'Forty cabinets on free play until half eleven, plus CRT console stations and a repair clinic in the corner.', when: 'Thu 20:00', room: 'Gaming Hall', price: 'Included', status: 'Open', hot: false, level: '1' },
    { id: 'e4', ref: 's17', kicker: 'Sunday · free', title: 'Bring-Your-Own-Dice Tavern', desc: 'Twenty open tables, rotating GMs, one-shots on the half hour. Ivo Kesler plays pickup games all afternoon.', when: 'Sun 12:00', room: 'Gaming Hall', price: 'Free', status: 'Drop in', hot: false, level: '1' },
    { id: 'e5', ref: 's18', kicker: 'Sunday · charity', title: 'Charity auction: props & pages', desc: 'Screen-used maquettes from Emil Brandt and original Hollow Bell pages. Proceeds to the city literacy fund.', when: 'Sun 14:00', room: 'Main Stage', price: 'Free to attend', status: 'Preview from 13:00', hot: false, level: '1' },
  ],

  exhibitorZones: [
    { label: 'All', key: 'all' },
    { label: 'Artist Alley', key: 'artist' },
    { label: 'Comics & Print', key: 'comics' },
    { label: 'Gaming Hall', key: 'gaming' },
    { label: 'Makers & Crafts', key: 'crafts' },
  ],

  exhibitors: [
    { name: 'Inkwell & Iron', booth: 'A-04', zone: 'artist', note: 'Prints and same-day sketch commissions, two artists at the table.', offers: ['Original art', 'Prints'], exclusive: '' },
    { name: 'Hollow Bell Art', booth: 'A-12', zone: 'artist', note: 'Nia Tsukamoto signs Fri & Sat 2–4pm. Limit three items.', offers: ['Manga art', 'Signing'], exclusive: 'Foil set, 200 run' },
    { name: 'Pale Fox Press', booth: 'A-19', zone: 'artist', note: 'Risograph minicomics and zines, $6–14, plus a make-your-own table.', offers: ['Zines', 'Workshops'], exclusive: '' },
    { name: 'Third Panel Comics', booth: 'C-02', zone: 'comics', note: 'Bronze and copper age back issues with a graded wall behind the counter.', offers: ['Back issues', 'Graded'], exclusive: '' },
    { name: 'Vault 12', booth: 'C-07', zone: 'comics', note: 'Key issues, CGC slabs, and on-site buy/sell/trade appraisals.', offers: ['Key issues', 'Appraisals'], exclusive: '' },
    { name: 'Meridian Books', booth: 'C-10', zone: 'comics', note: 'Publisher table with hourly signings and advance copies.', offers: ['Graphic novels', 'Signing'], exclusive: 'Hexwood ARC' },
    { name: 'Dice Tavern', booth: 'G-01', zone: 'gaming', note: 'Open tables, learn-to-play every thirty minutes, dice by the scoop.', offers: ['Tabletop', 'Demos'], exclusive: '' },
    { name: 'Cartridge Row', booth: 'G-05', zone: 'gaming', note: 'Imports, repairs while you wait, and four CRT stations.', offers: ['Retro games', 'Repairs'], exclusive: '' },
    { name: 'Ashen Reach Booth', booth: 'G-08', zone: 'gaming', note: 'Playable vertical slice plus a dev Q&A on the half hour.', offers: ['Demo', 'Dev Q&A'], exclusive: 'Enamel pin drop' },
    { name: 'Forge & Foam', booth: 'M-03', zone: 'crafts', note: 'Cosplay armor supplies and a free repair bar — bring your breakages.', offers: ['Cosplay', 'Repair bar'], exclusive: '' },
    { name: 'Brass Owl Props', booth: 'M-06', zone: 'crafts', note: 'Screen-accurate replica builds, commission list open Friday.', offers: ['Props', 'Commissions'], exclusive: '' },
    { name: 'Wick & Wax', booth: 'M-11', zone: 'crafts', note: 'Fandom-scent candle sets, three-packs and a blend-your-own bar.', offers: ['Crafts', 'Gifts'], exclusive: 'Con blend' },
  ],

  venue: {
    name: 'Riverfront Center',
    zones: {
      artist: { label: 'Artist Alley', color: 0x3f424d },
      comics: { label: 'Comics & Print', color: 0x44475a },
      gaming: { label: 'Gaming Hall', color: 0x3a3d4a },
      crafts: { label: 'Makers & Crafts', color: 0x474a58 },
      photo: { label: 'Photo Ops', color: 0x5d5294 },
      stage: { label: 'Main Stage', color: 0x423a6a },
      panels: { label: 'Panel Rooms', color: 0x3f424d },
      autos: { label: 'Autograph Row', color: 0x5d5294 },
    },
    // Blocks are generated from the same grid the prototype used — see venue/layout.ts.
    levels: [],
  },

  onboarding: {
    steps: [
      {
        title: 'Four days. Six hundred hours of programming.',
        sub: 'Nocturne Con 2026 runs Thursday to Sunday across two levels of the Riverfront Center. Set up once and the app filters everything to what you actually came for.',
      },
      {
        title: 'What are you here for?',
        sub: 'Pick as many as you like. This drives your recommendations, the match flags on panels, and which booths surface first.',
      },
      {
        title: 'Which days are you attending?',
        sub: 'Your schedule, photo op slots and queue reminders are scoped to these days.',
      },
    ],
    pitch: [
      { t: 'Talent, panels and photo ops in one place', s: '64 guests, 210 sessions, and every studio slot bookable in the app.' },
      { t: 'A schedule that knows where you are', s: 'Switch between time and room views; get a nudge 20 minutes before anything you starred.' },
      { t: 'A 3D floor you can actually read', s: 'Rotate the hall, tap a block, see who is in it and what they have.' },
      { t: 'Special events, not just panels', s: 'Second breakfasts, lock-ins, runways and tavern tables — all ticketed here.' },
    ],
    interestGroups: [
      { label: 'Genres', items: ['Sci-Fi', 'Fantasy', 'Horror', 'Anime', 'Comics', 'Tabletop', 'Video games'] },
      { label: 'Fandoms', items: ['Starfall', 'Hollow Bell', 'Ashen Reach', 'Halfling Chronicles', 'Hexwood', 'Sixth Sense TCG'] },
      { label: 'What you do here', items: ['Cosplay', 'Collecting', 'Original art', 'Autographs', 'Photo ops', 'Play games', 'Panels only'] },
    ],
    defaultInterests: ['Sci-Fi', 'Fantasy', 'Comics', 'Starfall', 'Photo ops', 'Cosplay'],
    defaultDays: ['fri', 'sat', 'sun'],
  },

  quests: {
    tiers: [{ n: 'Bronze', at: 0 }, { n: 'Silver', at: 500 }, { n: 'Gold', at: 1000 }],
    startingPoints: 340,
    quiz: [
      { q: 'Which room hosts Second Breakfast on Saturday?', a: ['Grand Ballroom', 'Main Stage', 'Gaming Hall'], correct: 0, points: 50 },
      { q: 'How many guests are on this year’s roster?', a: ['48', '64', '82'], correct: 1, points: 50 },
      { q: 'Where do photo op prints get collected?', a: ['Print desk, 30 min after the shoot', 'At the studio door', 'Posted to you after the con'], correct: 0, points: 50 },
    ],
    challenges: [
      { id: 'c1', label: 'Scan five exhibitor booths', note: 'Tap the badge reader at any table', have: 3, need: 5, pts: 120 },
      { id: 'c2', label: 'Attend panels in three tracks', note: 'Panel, Workshop, Screening or Gaming', have: 2, need: 3, pts: 150 },
      { id: 'c3', label: 'Check in at a special event', note: 'Second Breakfast counts', have: 1, need: 1, pts: 100 },
      { id: 'c4', label: 'Walk both levels of the venue', note: 'Level 1 hall and level 2 panels', have: 1, need: 2, pts: 60 },
    ],
    prizes: [
      { id: 'p1', label: 'Nocturne Con enamel pin set', note: 'Collect at the info desk, any day', cost: 150 },
      { id: 'p2', label: 'Con-exclusive print — Inkwell & Iron', note: 'Booth A-04, while the 200 run lasts', cost: 300 },
      { id: 'p3', label: 'Priority queue pass, one photo op', note: 'Skips the studio line once', cost: 500 },
      { id: 'p4', label: 'Signed Hollow Bell vol. 19', note: 'Draw entry, five copies', cost: 900 },
    ],
    consents: [
      { id: 'k1', label: 'Share badge contact with exhibitors I scan', note: 'Name and email only — no phone number' },
      { id: 'k2', label: 'Enter the grand prize draw', note: 'Signed prop crate, drawn Sunday 16:00' },
      { id: 'k3', label: 'Remind me about next year’s presale', note: 'One email, February' },
    ],
  },

  badge: {
    holder: 'Alex Reyes',
    code: 'NC26-4D-08871',
    kind: '4-day badge',
    email: 'alex.reyes@example.com',
  },

  slotTimes: ['10:20', '11:00', '11:40', '13:20', '14:00', '14:40', '16:20', '17:00', '17:40'],

  quickLinks: [
    { label: 'Special events', note: '5 this weekend', target: 'events' },
    { label: 'Photo ops', note: 'Book a studio slot', target: 'talent' },
    { label: 'Exhibitor hall', note: '312 booths', target: 'hall:exhibitors' },
    { label: '3D venue map', note: 'Two levels', target: 'hall:map' },
  ],

  strings: {
    homeNextKicker: 'Up next',
    scheduleGridHint:
      'Tap a block to star it. Rooms run top to bottom, the clock runs left to right — the same data as the list view, laid out for spotting gaps and clashes.',
    mapHint: 'drag · pinch · tap a block',
    mapEmpty:
      'Nothing selected. Tap any block on the floor to see who is in it — or filter by zone above to dim the rest of the hall.',
    eventsIntro:
      'Ticketed and free extras outside the panel tracks. Capacity shown live; wristbands are collected at the door.',
    cartFooter: 'Vouchers appear in Me · scan at the studio entrance',
    bookingSlotNote:
      'Studio queues open 20 minutes before each slot. Late arrivals are moved to the next opening with space.',
    bookingReviewNote:
      'Adding to cart holds the slot for 10 minutes. Photo op prints are collected at the print desk 30 minutes after the shoot; autographs are signed in person.',
    consentIntro:
      'Your badge carries these permissions. Exhibitors and the prize draw only ever see what you switch on here.',
    emptyVouchers: 'No photo ops or autographs booked yet. Open a talent profile to buy one.',
    emptySaved: 'Star anything on the schedule and it lands here, sorted by day.',
    emptyCart: 'Your cart is empty. Photo ops and autographs are booked from a talent profile.',
    callSheetTitle: 'Your call sheet',
    callSheetKicker: 'Planned around your interests',
    callSheetEmpty: 'Add a few interests and we\u2019ll build you a running order for the day.',
    callSheetError: 'Could not reach the planner. Showing interest matches instead.',
    searchPlaceholder: 'Ask about guests, panels, booths\u2026',
    searchIntro: 'Ask in plain language. Everything here comes from this weekend\u2019s programme.',
    searchSuggestionsLabel: 'Try asking',
    searchEmpty: 'No matches in the programme. Try a guest name, a room, or a fandom.',
    searchError: 'Offline \u2014 showing keyword matches from the programme instead.',
    searchDisclaimer: 'Answers are generated. Check the schedule before you queue.',
    searchRateLimited: 'Busy right now \u2014 showing keyword matches. Try the question again shortly.',
    searchUnconfigured: 'The assistant is offline. Showing keyword matches from the programme.',
    searchNoEndpoint: 'Search assistant not configured for this build. Showing keyword matches.',
    searchBadFormat: 'The assistant replied in an unexpected format. Showing keyword matches.',
    themePickerTitle: 'Appearance',
    themePickerNote: 'Pick how the app looks. Your schedule and bookings are unaffected.',
    themeShuffleLabel: 'Surprise me each launch',
    themeShuffleNote: 'Opens on a different look every time you come back.',
    promosLabel: 'What\u2019s on',
  },
};
